import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, HardHat, Plus, X } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const plusMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x.toISOString().slice(0, 10); };

export default function BulkEpiDialog({ open, employees, onClose, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [custom, setCustom] = useState("");
  const [dates, setDates] = useState({ delivery_date: "", expiry: "", review_date: "", next_review: "" });
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = today();
    setItems([]); setCustom("");
    setDates({ delivery_date: t, expiry: "", review_date: t, next_review: plusMonths(t, 12) });
    setSelected(employees.filter((e) => e.active !== false).map((e) => e.id));
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [open, employees]);

  const toggleItem = (c) => setItems((s) => (s.some((i) => i.id === c.id) ? s.filter((i) => i.id !== c.id) : [...s, c]));
  const addCustom = () => {
    if (!custom.trim()) return;
    setItems((s) => [...s, { id: `custom-${Date.now()}`, name: custom.trim(), brand: "", kind: "epi" }]);
    setCustom("");
  };
  const toggleEmp = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const setDate = (k, v) => setDates((d) => ({ ...d, [k]: v, ...(k === "review_date" && v ? { next_review: plusMonths(v, 12) } : {}) }));

  const save = async () => {
    if (items.length === 0) return toast.error("Selecciona al menos un EPI o prenda");
    if (selected.length === 0) return toast.error("Selecciona al menos un empleado");
    setSaving(true);
    try {
      const r = await api.post("/epis/bulk", { ...dates, employee_ids: selected, items });
      toast.success(`Entrega registrada: ${r.data.added} elementos en ${r.data.employees} empleados`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo registrar la entrega"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] flex flex-col p-4 sm:p-6" data-testid="bulk-epi-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><HardHat className="h-5 w-5 text-accent" /> Entrega masiva de EPIs y ropa</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 overflow-y-auto pr-1 flex-1">
          <div className="space-y-3">
            <Label>Catálogo ({items.length} seleccionados)</Label>
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="bulk-epi-catalog">
              {catalog.map((c) => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" checked={items.some((i) => i.id === c.id)} onChange={() => toggleItem(c)}
                    data-testid={`bulk-epi-item-${c.id}`} />
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.brand && <span className="text-xs text-muted-foreground">{c.brand}</span>}
                  <span className={`text-[10px] px-1.5 rounded ${c.kind === "ropa" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>{c.kind === "ropa" ? "Ropa" : "EPI"}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Otro EPI no catalogado…" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="bulk-epi-custom-input"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())} />
              <Button type="button" variant="outline" size="icon" onClick={addCustom}><Plus className="h-4 w-4" /></Button>
            </div>
            {items.filter((i) => String(i.id).startsWith("custom-")).map((i) => (
              <span key={i.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-2 py-0.5 mr-1">{i.name}
                <X className="h-3 w-3 cursor-pointer" onClick={() => toggleItem(i)} /></span>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Fecha entrega</Label>
                <Input type="date" value={dates.delivery_date} onChange={(e) => setDate("delivery_date", e.target.value)} data-testid="bulk-epi-delivery-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Caducidad (vacío = por uso)</Label>
                <Input type="date" value={dates.expiry} onChange={(e) => setDate("expiry", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Fecha revisado</Label>
                <Input type="date" value={dates.review_date} onChange={(e) => setDate("review_date", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Próxima revisión</Label>
                <Input type="date" value={dates.next_review} onChange={(e) => setDate("next_review", e.target.value)} /></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Empleados ({selected.length})</Label>
              <div className="flex gap-2 text-xs">
                <button className="text-accent hover:underline" onClick={() => setSelected(employees.map((e) => e.id))}>Todos</button>
                <button className="text-muted-foreground hover:underline" onClick={() => setSelected([])}>Ninguno</button>
              </div>
            </div>
            <div className="border rounded-lg overflow-y-auto max-h-[420px] divide-y" data-testid="bulk-epi-employees">
              {employees.map((e) => (
                <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" checked={selected.includes(e.id)} onChange={() => toggleEmp(e.id)} data-testid={`bulk-epi-emp-${e.id}`} />
                  <span className="flex-1 truncate">{e.name}</span>
                  <span className="text-[10px] text-muted-foreground">ropa {e.clothing_size || "—"} · calzado {e.shoe_size || "—"}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="bulk-epi-save-button">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Registrar entrega ({items.length} × {selected.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
